/** A name's words, compared without case, accents, punctuation or order ("Khalil, Omar" = "Omar KHALIL"). */
export function nameWords(name: string): string[] {
  return name
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .replace(/['-]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 0)
    .sort();
}

export type MatchStrength = 'same' | 'close';

/**
 * Whether two names may be the same person: 'same' when every word matches; 'close' when the
 * shorter name's words (at least two) are all in the longer one ("Omar Khalil" and
 * "Omar Ahmed Khalil"). Undefined otherwise. A person always decides.
 */
export function compareNames(a: string, b: string): MatchStrength | undefined {
  const x: string[] = nameWords(a);
  const y: string[] = nameWords(b);
  if (!x.length || !y.length) {
    return undefined;
  }
  if (x.join(' ') === y.join(' ')) {
    return 'same';
  }
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  return short.length >= 2 && short.every(w => long.indexOf(w) !== -1) ? 'close' : undefined;
}

export interface IConflictCandidate {
  /** "Client", "Person on an application" … */
  where: string;
  name: string;
  /** What it's linked to, e.g. the client or application it belongs to. */
  detail?: string;
  /** The client's key, when the candidate is a client. */
  key?: string;
}

export interface IConflictHit extends IConflictCandidate {
  strength: MatchStrength;
  matched: string;
}

/** Everyone in the firm's records whose name (or other names) may be one of these names. */
export function findConflicts(names: string[], candidates: { candidate: IConflictCandidate; names: string[] }[]): IConflictHit[] {
  const hits: IConflictHit[] = [];
  for (const c of candidates) {
    let best: IConflictHit | undefined;
    for (const n of names.filter(x => x.trim())) {
      for (const m of c.names.filter(x => x.trim())) {
        const s: MatchStrength | undefined = compareNames(n, m);
        if (s && (!best || (s === 'same' && best.strength === 'close'))) {
          best = { ...c.candidate, strength: s, matched: n };
        }
      }
    }
    if (best) {
      hits.push(best);
    }
  }
  return hits.sort((a, b) => (a.strength === b.strength ? a.name.localeCompare(b.name) : a.strength === 'same' ? -1 : 1));
}

/** "Other names" as written by people: separated by semicolons or new lines. */
export function splitNames(s: string | undefined): string[] {
  return (s ?? '').split(/[;\n]/).map(x => x.trim()).filter(x => !!x);
}
